import Close from '@/src/assets/icons/Close'
import BottomSheet, { BottomSheetScrollView } from '@gorhom/bottom-sheet'
import {
  forwardRef,
  useImperativeHandle,
  useRef,
  type ComponentRef,
  type PropsWithChildren,
} from 'react'
import { TouchableOpacity, View, ViewStyle, Text, StyleSheet } from 'react-native'
import type { BottomSheetProps } from '@gorhom/bottom-sheet'
import { mesaColorNames, typography, classes } from '@/src/common/theme'

/**
 * When using this component, you need to wrap the screen that this sheet slides in from in a
 * GestureHandlerRootView.
 */
export const SlideInModal = forwardRef<
  ComponentRef<typeof BottomSheet> | undefined,
  PropsWithChildren & {
    title?: string
    headerStyle?: ViewStyle
    showClose?: boolean
  } & BottomSheetProps
>(
  (
    { children, title, backgroundStyle, containerStyle, headerStyle, showClose = true, ...props },
    ref,
  ) => {
    const bottomSheetRef = useRef<ComponentRef<typeof BottomSheet>>(null)
    useImperativeHandle(ref, () => bottomSheetRef.current ?? undefined)

    return (
      <BottomSheet
        ref={bottomSheetRef}
        index={-1}
        enablePanDownToClose
        enableDynamicSizing
        backgroundStyle={[styles.background, backgroundStyle]}
        {...props}
      >
        <BottomSheetScrollView
          style={styles.container}
          contentContainerStyle={[{ paddingBottom: 88 }, containerStyle]}
        >
          <View style={[styles.header, headerStyle]}>
            {showClose && (
              <TouchableOpacity
                onPress={() => bottomSheetRef.current?.close()}
                style={styles.flex1}
                testID="close-modal-x-button"
              >
                <Close />
              </TouchableOpacity>
            )}
            {title ? (
              <Text style={[typography['heading/heading3'], styles.title]}>{title}</Text>
            ) : null}
            <View style={styles.flex1} />
          </View>
          {children}
        </BottomSheetScrollView>
      </BottomSheet>
    )
  },
)

const styles = StyleSheet.create({
  background: {
    backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
    padding: 10,
    borderRadius: 16,
    ...classes.cardShadow,
  },
  container: {
    padding: 10,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: 16,
  },
  title: {
    textAlign: 'center',
  },
  flex1: { flex: 1 },
})
